import moment from 'moment'
import React, { useMemo, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

import CheckboxField from './CheckboxField'
import RHFDatePickerInput from './RHFDatePickerInput'
import TimeInput from './TimeInput'

import Group from 'components/utils/Group'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { addDays, dateIsBefore } from 'lib/datetime/dateUtils'

interface Props {
  startDateLabel: string
  endDateLabel: string
  hideSameCheck?: boolean
  defaultMonth?: number
  defaultYear?: number
}

function StartEndDateTimeFields(props: Props) {
  const {
    endDateLabel,
    startDateLabel,
    hideSameCheck,
    defaultMonth,
    defaultYear,
  } = props
  const intl = useIntl()
  const { watch, register, getValues, setValue } = useFormContext()

  const startDate = watch('startDate')
  const isEndDateSame = watch('isEndDateSame')

  const earliestEndDate = useMemo(
    () => (startDate ? addDays(new Date(startDate), -1) : undefined),
    [startDate],
  )
  const onStartDateChange = useCallback(
    (newDate?: Date) => {
      const allValues = getValues()
      const endDate = allValues.endDate

      const hasNoEndDate = !!(!endDate && newDate)
      const endDateIsBeforeStart = !!(
        endDate &&
        newDate &&
        dateIsBefore(new Date(endDate), newDate)
      )
      if (hasNoEndDate || allValues.isEndDateSame || endDateIsBeforeStart) {
        // auto fill/keep our end date in sync when it's empty or invalid
        setValue('endDate', moment(newDate).format(ISO_DATE_FORMAT))
      }
    },
    [getValues, setValue],
  )

  const onEndDateChange = useCallback(
    (newDate?: Date) => {
      const allValues = getValues()
      if (!allValues.startDate && newDate) {
        setValue('startDate', moment(newDate).format(ISO_DATE_FORMAT))
      }
    },
    [getValues, setValue],
  )

  const onIsEndSameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setValue('isEndDateSame', e.currentTarget.checked)
      setValue('endDate', moment(startDate).format(ISO_DATE_FORMAT))

      if (e.currentTarget.checked && startDate) {
        setValue('endDate', startDate)
      }
    },
    [setValue, startDate],
  )

  return (
    <>
      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        <RHFDatePickerInput
          name="startDate"
          label={startDateLabel}
          onDateChange={onStartDateChange}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('startTime')}
          label={intl.formatMessage({
            id: '0rhaTB3BWOBAc-tHNtvl',
            defaultMessage: 'Time',
            description: 'Form label for start time field in date/time fields',
          })}
          placeholder={intl.formatMessage({
            id: '2T-ZnjV0bTdkkg0eIi7e',
            defaultMessage: 'Select time',
            description: 'Placeholder text for time picker in date/time fields',
          })}
        />
      </Group>

      {!hideSameCheck && (
        <CheckboxField
          {...register('isEndDateSame')}
          onChange={onIsEndSameChange}
        >
          {intl.formatMessage(
            {
              id: 'sDS27oyKGAeZgstHA-mD',
              defaultMessage: '{endDateLabel} same as {startDateLabel}',
              description: 'Checkbox label indicating end date/time is the same as start date/time',
            },
            { endDateLabel, startDateLabel: startDateLabel.toLowerCase() },
          )}
        </CheckboxField>
      )}

      <Group direction="horizontal" horizontalAlign="stretch" gap={16}>
        {/* @ts-ignore default values for props of class component */}
        <RHFDatePickerInput
          name="endDate"
          label={endDateLabel}
          onDateChange={onEndDateChange}
          min={earliestEndDate}
          disabled={isEndDateSame}
          defaultMonth={defaultMonth}
          defaultYear={defaultYear}
        />
        <TimeInput
          {...register('endTime')}
          label={intl.formatMessage({
            id: 'LSs3klBGLh6_p1Paq9Js',
            defaultMessage: 'Time',
            description: 'Form input label for end time field in date/time fields',
          })}
          placeholder={intl.formatMessage({
            id: '7-U6SHlhznYAOWLuw_-F',
            defaultMessage: 'Select time',
            description: 'Placeholder text for end time picker in date/time fields',
          })}
        />
      </Group>
    </>
  )
}

export default StartEndDateTimeFields
